import { createContext, useContext, useId } from 'react';
import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

type FieldState = {
  inputId: string;
  describedBy: string | undefined;
  invalid: boolean;
};

const FieldContext = createContext<FieldState | null>(null);

/**
 * Read by the input primitives so they wire up their own `id`,
 * `aria-describedby` and `aria-invalid` without the caller repeating them.
 * Returns null outside a Field, which is a legitimate use.
 */
export function useFieldState(): FieldState | null {
  return useContext(FieldContext);
}

/*
 * Optional props here are written `?: T | undefined` rather than `?: T`.
 *
 * `exactOptionalPropertyTypes` is on, and it is worth having on the record
 * types — for `variant` and `side`, "absent" and "undefined" genuinely differ.
 * For component props they do not: a caller passing `error={errors.x?.message}`
 * is the normal way to write this, and rejecting it buys nothing.
 */
type FieldProps = {
  label: string;
  /** Persistent guidance. Shown whether or not there is an error. */
  help?: ReactNode | undefined;
  error?: string | undefined;
  /**
   * Marks the field visually and for assistive technology. Read DATA_MODEL.md
   * before adding one — every required field costs adoption.
   */
  required?: boolean | undefined;
  children: ReactNode;
  className?: string | undefined;
};

export function Field({ label, help, error, required = false, children, className }: FieldProps) {
  const inputId = useId();
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const describedBy =
    [help === undefined ? null : helpId, error === undefined ? null : errorId]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ inputId, describedBy, invalid: error !== undefined }}>
      <div className={cx('flex flex-col gap-1.5', className)}>
        <label htmlFor={inputId} className="text-sm font-medium text-text">
          {label}
          {required ? (
            <span className="ml-1 text-danger" aria-hidden="true">
              *
            </span>
          ) : null}
          {required ? <span className="sr-only"> (required)</span> : null}
        </label>

        {children}

        {help === undefined ? null : (
          /*
           * The no-patient-identifiable-information line is the one sentence in
           * this app that must actually be read, and it used to sit on the
           * lowest-contrast text token there is. It now gets `--ui-text-notice`
           * at medium weight, with a small accent square in front of it so the
           * emphasis is carried by shape as well as by colour. Same string,
           * same position, same field. Every other kind of help stays faint.
           */
          <p
            id={helpId}
            className={
              help === NO_IDENTIFIABLE_INFORMATION
                ? 'flex items-baseline gap-1.5 text-xs font-medium text-notice'
                : 'text-xs text-faint'
            }
          >
            {help === NO_IDENTIFIABLE_INFORMATION ? (
              <span
                aria-hidden="true"
                className="mt-px size-1 shrink-0 self-center rounded-xs bg-accent"
              />
            ) : null}
            {help}
          </p>
        )}

        {error === undefined ? null : (
          <p id={errorId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/**
 * The standing reminder that sits under every free-text field in the app.
 * Wording is fixed by DATA_MODEL.md — do not reword it per screen.
 */
export const NO_IDENTIFIABLE_INFORMATION =
  'No patient identifiable information — no names, initials, NHS or hospital numbers.';
